import {
  S3Client,
  CreateBucketCommand,
  PutObjectCommand,
  DeleteBucketCommand,
  DeleteObjectCommand,
  DeleteObjectsCommand,
  ListObjectsV2Command,
  ListBucketsCommand,
  CopyObjectCommand
} from '@aws-sdk/client-s3'
import CryptoJS from 'crypto-js'
import { defineStore } from 'pinia'
import { S3_MAX_KEYS_LIMIT } from '@/configs/config'
import S3Object from '@/models/S3Object.model'
import S3Bucket from '@/models/S3Bucket.model'

function computeMD5 (body) {
  const hash = CryptoJS.MD5(body)
  return CryptoJS.enc.Base64.stringify(hash)
}

const addContentMD5Middleware = (next, context) => async (args) => {
  if (context.commandName !== 'DeleteObjectsCommand') {
    return next(args)
  }

  try {
    const request = args.request

    if (request && request.body) {
      const hash = computeMD5(request.body)

      args.request.headers = args.request.headers || {}
      args.request.headers['Content-MD5'] = hash
    }
  } catch (error) {
    console.debug('Ошибка вычисления MD5:', error)
  }

  return next(args)
}

export const useS3Store = defineStore('S3Store', {
  state: () => {
    return {
      s3Client: null,
      // old version v2
      S3: null,
      objects: [],
      params: {
        Bucket: null,
        Delimiter: '/',
        MaxKeys: S3_MAX_KEYS_LIMIT,
        Prefix: null
      },
      pagination: {
        params: null
      },
      isShowS3Actions: false
    }
  },

  actions: {
    initS3Client (id, key) {
      const isHTTPS = process.env.VUE_APP_S3_SSL_ENABLED === 'true'
      const s3Client = new S3Client({
        region: process.env.VUE_APP_S3_REGION,
        endpoint: `${isHTTPS ? 'https' : 'http'}://${process.env.VUE_APP_S3_ENDPOINT}`,
        forcePathStyle: process.env.VUE_APP_S3_FORCE_PATH_STYLE === 'true',
        sslEnabled: isHTTPS,
        credentials: {
          accessKeyId: id,
          secretAccessKey: key
        }
      })

      s3Client.middlewareStack.add(
        addContentMD5Middleware,
        {
          step: 'build'
        }
      )

      this.s3Client = s3Client
    },

    async getBuckets () {
      try {
        const response = await this.s3Client.send(new ListBucketsCommand({}))
        const buckets = []

        response.Buckets.forEach((bucket) => {
          buckets.push(new S3Bucket({
            ...bucket,
            accountID: response.Owner.ID,
            accountName: response.Owner.DisplayName
          }))
        })

        this.objects = buckets
      } catch (error) {
        console.debug('Get buckets list error:', error)
        throw error
      }
    },

    async getObjects (params, objects = []) {
      try {
        const cmd = new ListObjectsV2Command(params)
        const response = await this.s3Client.send(cmd)

        const prefix = params.Prefix

        if (response.CommonPrefixes) {
          response.CommonPrefixes.forEach(d => {
            const part = d.Prefix.slice(0, -1).split('/').reverse()
            const dir = new S3Object({
              ETag: d.ETag,
              name: part[0],
              Key: d.Prefix,
              type: 'folder'
            })

            objects.push(dir)
          })
        }

        if (response.Contents) {
          response.Contents.forEach(obj => {
            if (obj.Key === prefix) return

            objects.push(new S3Object({
              ...obj,
              name: obj.Key.replace(prefix, '')
            }))
          })
        }

        if (response.NextContinuationToken) {
          this.pagination = {
            params: {
              ...params,
              ContinuationToken: response.NextContinuationToken
            }
          }
        } else {
          this.pagination.params = null
        }

        this.objects = objects
      } catch (error) {
        console.debug('Get buckets list error:', error)
        throw error
      }
    },

    async createBucket (bucketName) {
      try {
        const command = new CreateBucketCommand({
          Bucket: bucketName
        })

        await this.s3Client.send(command)
        this.objects.push(new S3Bucket({
          Name: bucketName,
          CreationDate: new Date()
        }))
      } catch (error) {
        console.debug('Creating bucket error:', error)
        throw error
      }
    },

    async deleteBucket (bucketName) {
      try {
        const command = new DeleteBucketCommand({ Bucket: bucketName })
        await this.s3Client.send(command)

        this.removeObjectFromStore(bucketName)
        return true
      } catch (error) {
        console.debug('Remove bucket:', error)
        throw error
      }
    },

    async createFolder (folderKey, folderName) {
      try {
        const command = new PutObjectCommand({
          Bucket: this.params.Bucket,
          Key: folderKey,
          Body: new Uint8Array(0)
        })
        const response = await this.s3Client.send(command)

        this.objects.unshift(new S3Object({
          name: folderName,
          Key: folderKey,
          ETag: response.ETag,
          type: 'folder'
        }))
      } catch (error) {
        console.debug('Creating folder:', error)
        throw error
      }
    },

    async deleteObject (objectKey) {
      try {
        const command = new DeleteObjectCommand({
          Bucket: this.params.Bucket,
          Key: objectKey
        })

        await this.s3Client.send(command)
        this.removeObjectFromStoreByKey(objectKey)
      } catch (error) {
        console.debug('Remove object:', error)
        throw error
      }
    },

    async deleteObjects (objectKeys, removeOriginFromStore = true) {
      try {
        if (!Array.isArray(objectKeys) || objectKeys.length === 0) {
          throw new Error('Не указаны ключи объектов для удаления')
        }

        const command = new DeleteObjectsCommand({
          Bucket: this.params.Bucket,
          Delete: {
            Objects: objectKeys
          }
        })

        const { Deleted } = await this.s3Client.send(command)

        if (Deleted && removeOriginFromStore) {
          Deleted.forEach(i => {
            this.removeObjectFromStoreByKey(i.Key)
          })
        }
      } catch (error) {
        console.debug('Remove objects:', error)
        throw error
      }
    },

    copyObject (oldKey, newName) {
      const Bucket = this.params.Bucket
      const params = {
        Bucket,
        CopySource: `${Bucket}/${encodeURI(oldKey)}`,
        Key: newName
      }

      const cmd = new CopyObjectCommand(params)
      return this.s3Client.send(cmd)
    },

    checkExistS3Object (Key) {
      const params = {
        Bucket: this.params.Bucket,
        // Key: Key,
        Prefix: Key,
        MaxKeys: 1
      }

      const cmd = new ListObjectsV2Command(params)

      return this.s3Client.send(cmd).then((data) => {
        const found = data?.Contents?.some(item => item.Key === Key)
        return found
      }).catch(() => {
        return false
      })
    },

    setS3ToStore (data) {
      this.S3 = data
    },
    setObjectsToStore (objects) {
      this.objects = objects
    },

    addObjectToStore (object) {
      this.objects.push(object)
    },

    removeObjectFromStore (name) {
      const i = this.objects.findIndex(b => b.name === name)

      if (i >= 0) {
        this.objects.splice(i, 1)
      }
    },

    removeObjectFromStoreByKey (key) {
      const i = this.objects.findIndex(b => b.key === key)

      if (i >= 0) {
        this.objects.splice(i, 1)
      } else {

        /**
         * try remove folder
         */
        const i = this.objects.findIndex(b => b.key === key + '/')
        if (i >= 0) {
          this.objects.splice(i, 1)
        }
      }
    }
  }
})
